import { useState, useEffect, useRef } from 'react';
import { Container, Card, Form, Button, Alert, Tabs, Tab, Spinner, Row, Col } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { authService, User } from '../services/auth.service';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import NotificationSettingsCard from '../components/common/NotificationSettingsCard';
import { isBillingEnabled } from '../utils/config';

// 利用可能な言語リスト（翻訳キーを使用）
const getLanguages = (t: (key: string) => string) => [
  { code: 'ja', name: t('common.language_ja'), nativeName: '日本語' },
  { code: 'en', name: t('common.language_en'), nativeName: 'English' },
  { code: 'zh-CN', name: t('common.language_zh_cn'), nativeName: '中文（简体）' },
  { code: 'fr', name: t('common.language_fr'), nativeName: 'Français' },
  { code: 'es', name: t('common.language_es'), nativeName: 'Español' },
  { code: 'de', name: t('common.language_de'), nativeName: 'Deutsch' },
  { code: 'pt', name: t('common.language_pt'), nativeName: 'Português' },
  { code: 'ru', name: t('common.language_ru'), nativeName: 'Русский' },
];

// グループ化されたタイムゾーンリスト（旧システムのLocaleHelper::getGroupedTimezones()に合わせる）
const groupedTimezones = {
  'Asia': [
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
    { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT)' },
    { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
    { value: 'Asia/Seoul', label: 'Asia/Seoul (KST)' },
    { value: 'Asia/Bangkok', label: 'Asia/Bangkok (ICT)' },
    { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  ],
  'Europe': [
    { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
    { value: 'Europe/Dublin', label: 'Europe/Dublin (GMT/IST)' },
    { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
    { value: 'Europe/Rome', label: 'Europe/Rome (CET)' },
    { value: 'Europe/Madrid', label: 'Europe/Madrid (CET)' },
    { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET)' },
    { value: 'Europe/Brussels', label: 'Europe/Brussels (CET)' },
    { value: 'Europe/Vienna', label: 'Europe/Vienna (CET)' },
    { value: 'Europe/Zurich', label: 'Europe/Zurich (CET)' },
    { value: 'Europe/Stockholm', label: 'Europe/Stockholm (CET)' },
    { value: 'Europe/Oslo', label: 'Europe/Oslo (CET)' },
    { value: 'Europe/Copenhagen', label: 'Europe/Copenhagen (CET)' },
    { value: 'Europe/Helsinki', label: 'Europe/Helsinki (EET)' },
    { value: 'Europe/Warsaw', label: 'Europe/Warsaw (CET)' },
    { value: 'Europe/Prague', label: 'Europe/Prague (CET)' },
    { value: 'Europe/Athens', label: 'Europe/Athens (EET)' },
    { value: 'Europe/Istanbul', label: 'Europe/Istanbul (TRT)' },
    { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
  ],
  'America': [
    { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
    { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
    { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  ],
  'Oceania': [
    { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST)' },
    { value: 'Australia/Adelaide', label: 'Australia/Adelaide (ACST)' },
    { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
    { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST)' },
    { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu (HST)' },
  ],
  'Other': [
    { value: 'UTC', label: 'UTC' },
  ],
};

const createProfileSchema = (t: (key: string, opts?: Record<string, unknown>) => string) => z.object({
  firstName: z.string().min(1, t('profile.last_name') + ' ' + t('common.required', { defaultValue: 'は必須です' })),
  lastName: z.string().min(1, t('profile.first_name') + ' ' + t('common.required', { defaultValue: 'は必須です' })),
  email: z.string().email(t('register.invalid_email', { defaultValue: '有効なメールアドレスを入力してください' })).optional(),
  organizationName: z.string().optional(),
  departmentName: z.string().optional(),
  billingPostalCode: z.string().optional(),
  billingPrefecture: z.string().optional(),
  billingCity: z.string().optional(),
  billingAddressLine1: z.string().optional(),
  billingAddressLine2: z.string().optional(),
  billingPhone: z.string().optional(),
  billingInvoiceRegNumber: z.string().optional(),
});

const createPreferencesSchema = (t: (key: string, opts?: Record<string, unknown>) => string) => z.object({
  timezone: z.string().min(1, t('profile.timezone') + ' ' + t('common.required', { defaultValue: 'は必須です' })),
  language: z.enum(['ja', 'en', 'zh-CN', 'fr', 'es', 'de', 'pt', 'ru'], {
    errorMap: () => ({ message: t('profile.language') + ' ' + t('common.required', { defaultValue: 'は必須です' }) }),
  }),
});

const createPasswordSchema = (t: (key: string, opts?: Record<string, unknown>) => string) => z
  .object({
    currentPassword: z.string().min(1, t('profile.current_password') + ' ' + t('common.required', { defaultValue: 'は必須です' })),
    newPassword: z.string().min(8, t('profile.new_password') + ' ' + t('common.min_length', { defaultValue: 'は8文字以上である必要があります' })),
    confirmPassword: z.string().min(1, t('profile.confirm_password') + ' ' + t('common.required', { defaultValue: 'は必須です' })),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: t('profile.password_mismatch'),
    path: ['confirmPassword'],
  });

type ProfileFormData = z.infer<ReturnType<typeof createProfileSchema>>;
type PasswordFormData = z.infer<ReturnType<typeof createPasswordSchema>>;
type PreferencesFormData = z.infer<ReturnType<typeof createPreferencesSchema>>;

export default function Profile() {
  const { t, i18n } = useTranslation();
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  // デバウンス用のタイマー
  const emailCheckTimerRef = useRef<NodeJS.Timeout | null>(null);

  const profileSchema = createProfileSchema(t);
  const preferencesSchema = createPreferencesSchema(t);
  const passwordSchema = createPasswordSchema(t);

  const { data: userResponse, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authService.getMe,
    enabled: !!currentUser,
  });

  // ユーザーデータの取得（レスポンス構造に応じてフォールバック）
  // userResponseは { success: true, data: { user } } の構造
  // しかし、実際のレスポンスでは直接ユーザーオブジェクトになっている可能性がある
  let user: User | undefined;
  if (userResponse) {
    // まず通常の構造を確認: { success: true, data: { user } }
    if (userResponse.data?.user) {
      user = userResponse.data.user;
    } 
    // dataが直接ユーザーオブジェクトの場合（idプロパティがあるか確認）
    else if (userResponse.data && typeof userResponse.data === 'object' && 'id' in userResponse.data && 'email' in userResponse.data) {
      user = userResponse.data as unknown as User;
    } 
    // userResponse自体がユーザーオブジェクトの場合（successプロパティがないことを確認）
    else if (userResponse && typeof userResponse === 'object' && 'id' in userResponse && 'email' in userResponse && !('success' in userResponse)) {
      user = userResponse as User;
    }
  }


  const profileMutation = useMutation({
    mutationFn: (data: Partial<User>) => authService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      setSuccessMessage(t('profile.profile_updated'));
      setErrorMessage('');
    },
    onError: (err: any) => {
      setErrorMessage(err.response?.data?.error?.message || t('profile.update_failed'));
      setSuccessMessage('');
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authService.updatePassword(data),
    onSuccess: () => {
      setSuccessMessage(t('profile.password_changed'));
      setErrorMessage('');
    },
    onError: (err: any) => {
      setErrorMessage(err.response?.data?.error?.message || t('profile.password_update_failed'));
      setSuccessMessage('');
    },
  });

  const preferencesMutation = useMutation({
    mutationFn: (data: { timezone: string; language: string }) =>
      authService.updateProfile(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      // 言語を変更した場合は、i18nの言語も更新
      if (variables.language) {
        i18n.changeLanguage(variables.language);
      }
      // localStorageとCookieにも保存（他のページで引き継ぐため）
      localStorage.setItem('preferred_timezone', variables.timezone);
      localStorage.setItem('preferred_language', variables.language);
      localStorage.setItem('i18nextLng', variables.language);
      document.cookie = `preferred_timezone=${variables.timezone}; max-age=${30 * 24 * 60 * 60}; path=/`;
      document.cookie = `preferred_language=${variables.language}; max-age=${30 * 24 * 60 * 60}; path=/`;
      setSuccessMessage(t('profile.preferences_saved'));
      setErrorMessage('');
    },
    onError: (err: any) => {
      setErrorMessage(err.response?.data?.error?.message || t('profile.preferences_save_failed'));
      setSuccessMessage('');
    },
  });

  // フォームの初期値をユーザーデータから生成
  const profileDefaultValues = user && user.id ? {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    organizationName: user.organizationName || '',
    departmentName: user.departmentName || '',
    billingPostalCode: user.billingPostalCode || '',
    billingPrefecture: user.billingPrefecture || '',
    billingCity: user.billingCity || '',
    billingAddressLine1: user.billingAddressLine1 || '',
    billingAddressLine2: user.billingAddressLine2 || '',
    billingPhone: user.billingPhone || '',
    billingInvoiceRegNumber: user.billingInvoiceRegNumber || '',
  } : {
    firstName: '',
    lastName: '',
    email: '',
    organizationName: '',
    departmentName: '',
    billingPostalCode: '',
    billingPrefecture: '',
    billingCity: '',
    billingAddressLine1: '',
    billingAddressLine2: '',
    billingPhone: '',
    billingInvoiceRegNumber: '',
  };

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    reset: resetProfile,
    setValue: setProfileValue,
    watch: watchProfile,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: profileDefaultValues,
    values: user && user.id ? {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      organizationName: user.organizationName || '',
      departmentName: user.departmentName || '',
      billingPostalCode: user.billingPostalCode || '',
      billingPrefecture: user.billingPrefecture || '',
      billingCity: user.billingCity || '',
      billingAddressLine1: user.billingAddressLine1 || '',
      billingAddressLine2: user.billingAddressLine2 || '',
      billingPhone: user.billingPhone || '',
      billingInvoiceRegNumber: user.billingInvoiceRegNumber || '',
    } : undefined,
  });

  const watchedEmail = watchProfile('email') || '';

  // メールアドレスの重複チェック（デバウンス付き）
  useEffect(() => {
    if (emailCheckTimerRef.current) {
      clearTimeout(emailCheckTimerRef.current);
    }

    if (!watchedEmail || !user || watchedEmail === user.email) {
      setEmailError('');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(watchedEmail)) {
      setEmailError('');
      return;
    }

    emailCheckTimerRef.current = setTimeout(async () => {
      setIsCheckingEmail(true);
      try {
        const result = await authService.checkEmailExists(watchedEmail);
        if (result.data.exists) {
          setEmailError(t('register.email_already_registered', { defaultValue: 'このメールアドレスは既に登録されています' }));
        } else {
          setEmailError('');
        }
      } catch (err) {
        setEmailError('');
      } finally {
        setIsCheckingEmail(false);
      }
    }, 500);

    return () => {
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current);
      }
    };
  }, [watchedEmail, user, t]);


  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const {
    register: registerPreferences,
    handleSubmit: handleSubmitPreferences,
    reset: resetPreferences,
    formState: { errors: preferencesErrors },
  } = useForm<PreferencesFormData>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: {
      timezone: user?.timezone || 'Asia/Tokyo',
      language: (user?.language as 'ja' | 'en' | 'zh-CN' | 'fr' | 'es' | 'de' | 'pt' | 'ru') || 'ja',
    },
  });

  // ユーザーデータが読み込まれたらフォームをリセット
  useEffect(() => {
    if (!isLoading && user && user.id) {
      const profileData = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        organizationName: user.organizationName || '',
        departmentName: user.departmentName || '',
        billingPostalCode: user.billingPostalCode || '',
        billingPrefecture: user.billingPrefecture || '',
        billingCity: user.billingCity || '',
        billingAddressLine1: user.billingAddressLine1 || '',
        billingAddressLine2: user.billingAddressLine2 || '',
        billingPhone: user.billingPhone || '',
        billingInvoiceRegNumber: user.billingInvoiceRegNumber || '',
      };
      
      // フォームをリセット（既存の値を上書き）
      resetProfile(profileData, { keepDefaultValues: false });
      
      // 念のため、各フィールドも個別に設定（確実に反映される）
      setProfileValue('firstName', profileData.firstName, { shouldValidate: false, shouldDirty: false });
      setProfileValue('lastName', profileData.lastName, { shouldValidate: false, shouldDirty: false });
      setProfileValue('email', profileData.email, { shouldValidate: false, shouldDirty: false });
      setProfileValue('organizationName', profileData.organizationName, { shouldValidate: false, shouldDirty: false });
      setProfileValue('departmentName', profileData.departmentName, { shouldValidate: false, shouldDirty: false });
      setProfileValue('billingPostalCode', profileData.billingPostalCode, { shouldValidate: false, shouldDirty: false });
      setProfileValue('billingPrefecture', profileData.billingPrefecture, { shouldValidate: false, shouldDirty: false });
      setProfileValue('billingCity', profileData.billingCity, { shouldValidate: false, shouldDirty: false });
      setProfileValue('billingAddressLine1', profileData.billingAddressLine1, { shouldValidate: false, shouldDirty: false });
      setProfileValue('billingAddressLine2', profileData.billingAddressLine2, { shouldValidate: false, shouldDirty: false });
      setProfileValue('billingPhone', profileData.billingPhone, { shouldValidate: false, shouldDirty: false });
      setProfileValue('billingInvoiceRegNumber', profileData.billingInvoiceRegNumber, { shouldValidate: false, shouldDirty: false });
      
      resetPreferences({
        timezone: user.timezone || 'Asia/Tokyo',
        language: (user.language as 'ja' | 'en' | 'zh-CN' | 'fr' | 'es' | 'de' | 'pt' | 'ru') || 'ja',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, user?.id]); // isLoadingがfalseになり、user.idが存在するときに実行

  const onSubmitProfile = async (data: ProfileFormData) => {
    // 重複チェックエラーがある場合は送信を阻止
    if (emailError) {
      setErrorMessage(emailError);
      return;
    }
    await profileMutation.mutateAsync(data);
  };

  const onSubmitPassword = async (data: PasswordFormData) => {
    await passwordMutation.mutateAsync({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
    resetPassword();
  };

  const onSubmitPreferences = async (data: PreferencesFormData) => {
    await preferencesMutation.mutateAsync(data as Parameters<typeof preferencesMutation.mutateAsync>[0]);
  };

  if (isLoading) {
    return (
      <Container className="mt-4">
        <h1 className="mb-4">{t('profile.title')}</h1>
        <div className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading', { defaultValue: '読み込み中...' })}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <h1 className="mb-4">{t('profile.title')}</h1>

      {/* 使い方の説明 */}
      <CollapsibleHelpSection
        titleKey="profile.how_to_use_title"
        descriptionKey="profile.how_to_use_desc"
        stepsKey={[
          'profile.how_to_use_step1',
          'profile.how_to_use_step2',
          'profile.how_to_use_step3',
          'profile.how_to_use_step4',
        ]}
        storageKey="profile"
      />

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="danger" dismissible onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <div style={{ marginBottom: '2rem' }}>
        <style>{`
          .profile-tabs .nav-link {
            color: #495057 !important;
            background-color: #f8f9fa !important;
            border: 1px solid #dee2e6 !important;
            border-bottom: none !important;
            margin-right: 0.25rem;
            font-weight: 500;
            padding: 0.75rem 1.5rem;
            transition: all 0.2s ease;
          }
          .profile-tabs .nav-link:hover {
            color: #0d6efd !important;
            background-color: #e9ecef !important;
            border-color: #dee2e6 !important;
          }
          .profile-tabs .nav-link.active {
            color: #0d6efd !important;
            background-color: #ffffff !important;
            border-color: #dee2e6 #dee2e6 #ffffff !important;
            border-bottom: 2px solid #ffffff !important;
            font-weight: 600;
          }
          .profile-tabs .nav-tabs {
            border-bottom: 2px solid #dee2e6;
            margin-bottom: 0;
          }
        `}</style>
        <Tabs 
          defaultActiveKey="profile" 
          className="mb-4 profile-tabs"
        >
          <Tab 
            eventKey="profile" 
            title={t('profile.profile_info')}
          >
          <Card className="mt-3">
            <Card.Header>
              <Card.Title className="mb-0">
                <i className="fas fa-user me-2"></i>
                {t('profile.basic_info')}
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmitProfile(onSubmitProfile)}>
                {/* ロール情報セクション（読み取り専用） */}
                {user && (
                  <div className="mb-4">
                    <h5 className="mb-3 text-info">
                      <i className="fas fa-user-shield me-2"></i>
                      {t('profile.role', { defaultValue: '役割' })}
                    </h5>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label htmlFor="profile-role">
                            {t('profile.role', { defaultValue: '役割' })}
                          </Form.Label>
                          <Form.Control
                            id="profile-role"
                            type="text"
                            name="role"
                            value={
                              isAdminUser(user)
                                ? t('profile.role_admin', { defaultValue: '管理者' })
                                : user.roleId === 2
                                ? t('profile.role_user', { defaultValue: '一般ユーザー' })
                                : user.roleId === 3
                                ? t('profile.role_facility_manager', { defaultValue: '施設管理者' })
                                : user.roleId === 4
                                ? t('profile.role_pathogen_manager', { defaultValue: '病原体管理者' })
                                : user.role?.name || t('profile.role_user', { defaultValue: '一般ユーザー' })
                            }
                            readOnly
                            className="bg-light"
                            aria-label={t('profile.role', { defaultValue: '役割' })}
                          />
                          {user.role?.description && (
                            <Form.Text className="text-muted">
                              {user.role.description}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                )}

                <hr className="my-4" />

                {/* 基本情報セクション */}
                <div className="mb-4">
                  <h5 className="mb-3 text-primary">
                    <i className="fas fa-user me-2"></i>
                    {t('register.basic_info', { defaultValue: '基本情報' })}
                  </h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="profile-last-name">
                      {t('profile.last_name')} <span className="text-danger">*</span>
                    </Form.Label>
                        <Form.Control
                          id="profile-last-name"
                          type="text"
                          {...registerProfile('lastName')}
                          isInvalid={!!profileErrors.lastName}
                          autoComplete="family-name"
                        />
                        <Form.Control.Feedback type="invalid">
                          {profileErrors.lastName?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="profile-first-name">
                          {t('profile.first_name')} <span className="text-danger">*</span>
                        </Form.Label>
                    <Form.Control
                      id="profile-first-name"
                      type="text"
                      {...registerProfile('firstName')}
                      isInvalid={!!profileErrors.firstName}
                      autoComplete="given-name"
                    />
                    <Form.Control.Feedback type="invalid">
                      {profileErrors.firstName?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="profile-email">
                      {t('register.email_label', { defaultValue: 'メールアドレス' })}
                    </Form.Label>
                    <Form.Control
                      id="profile-email"
                      type="email"
                      {...registerProfile('email')}
                      isInvalid={!!profileErrors.email || !!emailError}
                      autoComplete="email"
                    />
                    {isCheckingEmail && (
                      <Form.Text className="text-muted">
                        <i className="fas fa-spinner fa-spin me-1"></i>
                        {t('register.checking_email', { defaultValue: '確認中...' })}
                      </Form.Text>
                    )}
                    {emailError && (
                    <Form.Control.Feedback type="invalid">
                        <i className="fas fa-exclamation-circle me-1"></i>
                        {emailError}
                    </Form.Control.Feedback>
                    )}
                    {!emailError && !isCheckingEmail && watchedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(watchedEmail) && watchedEmail !== user?.email && (
                      <Form.Text className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        {t('register.email_available', { defaultValue: 'このメールアドレスは使用可能です' })}
                      </Form.Text>
                    )}
                    {!emailError && !isCheckingEmail && (!watchedEmail || watchedEmail === user?.email) && (
                      <Form.Text className="text-muted">
                        {t('register.email_login_note', { defaultValue: 'ログインに使用します。' })}
                      </Form.Text>
                    )}
                  </Form.Group>
                </div>

                <hr className="my-4" />

                {/* 組織情報セクション */}
                <div className="mb-4">
                  <h5 className="mb-3 text-success">
                    <i className="fas fa-building me-2"></i>
                    {t('register.organization_section', { defaultValue: '組織情報（任意）' })}
                  </h5>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                    <Form.Label htmlFor="profile-organization">{t('profile.organization')}</Form.Label>
                        <Form.Control
                          id="profile-organization"
                          type="text"
                          {...registerProfile('organizationName')}
                          autoComplete="organization"
                          placeholder={t('register.organization_name_placeholder', { defaultValue: '例: 株式会社サンプル' })}
                        />
                        <Form.Text className="text-muted">
                          {t('register.organization_name_note', { defaultValue: '所属する組織名を入力してください' })}
                        </Form.Text>
                  </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                    <Form.Label htmlFor="profile-department">{t('profile.department')}</Form.Label>
                        <Form.Control
                          id="profile-department"
                          type="text"
                          {...registerProfile('departmentName')}
                          autoComplete="organization-title"
                          placeholder={t('register.department_name_placeholder', { defaultValue: '例: 研究開発部' })}
                        />
                        <Form.Text className="text-muted">
                          {t('register.department_name_note', { defaultValue: '所属する部署名を入力してください' })}
                        </Form.Text>
                  </Form.Group>
                    </Col>
                  </Row>
                </div>

                <hr className="my-4" />

                {/* 請求先情報セクション */}
                <div className="mb-4">
                  <h5 className="mb-3 text-info">
                    <i className="fas fa-receipt me-2"></i>
                    {t('register.billing_section_title', { defaultValue: '請求先情報（任意）' })}
                  </h5>
                  <p className="text-muted small mb-3">
                    {isBillingEnabled()
                      ? t('register.billing_section_note', { defaultValue: '請求書・領収書を発行する際に必要です。後からプロフィール画面で入力・更新することもできます。' })
                      : t('register.billing_section_note_free', { defaultValue: '将来的に請求書・領収書を発行する際に必要になります。現在は無料開放中のため任意ですが、後からプロフィール画面で入力・更新することもできます。' })}
                  </p>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="profile-billing-postal-code">{t('register.postal_code_label', { defaultValue: '郵便番号' })}</Form.Label>
                        <Form.Control
                          id="profile-billing-postal-code"
                          type="text"
                          {...registerProfile('billingPostalCode')}
                          autoComplete="postal-code"
                          pattern="[0-9]{3}-?[0-9]{4}"
                          placeholder={t('register.postal_code_placeholder', { defaultValue: '123-4567' })}
                        />
                        <Form.Text className="text-muted">{t('register.postal_code_example', { defaultValue: '例: 123-4567' })}</Form.Text>
                  </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="profile-billing-prefecture">{t('register.prefecture_label', { defaultValue: '都道府県' })}</Form.Label>
                        <Form.Control
                          id="profile-billing-prefecture"
                          type="text"
                          {...registerProfile('billingPrefecture')}
                          autoComplete="address-level1"
                          placeholder={t('register.prefecture_placeholder', { defaultValue: '東京都' })}
                        />
                        <Form.Text className="text-muted">{t('register.prefecture_example', { defaultValue: '例: 東京都' })}</Form.Text>
                  </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="profile-billing-city">{t('register.city_label', { defaultValue: '市区町村' })}</Form.Label>
                        <Form.Control
                          id="profile-billing-city"
                          type="text"
                          {...registerProfile('billingCity')}
                          autoComplete="address-level2"
                          placeholder={t('register.city_placeholder', { defaultValue: '千代田区' })}
                        />
                        <Form.Text className="text-muted">{t('register.city_example', { defaultValue: '例: 千代田区' })}</Form.Text>
                  </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="profile-billing-address-line1">{t('register.address_line1_label', { defaultValue: '番地・建物名' })}</Form.Label>
                        <Form.Control
                          id="profile-billing-address-line1"
                          type="text"
                          {...registerProfile('billingAddressLine1')}
                          autoComplete="street-address"
                          placeholder={t('register.address_line1_placeholder', { defaultValue: '1-2-3 サンプルビル' })}
                        />
                        <Form.Text className="text-muted">{t('register.address_line1_example', { defaultValue: '例: 1-2-3 サンプルビル' })}</Form.Text>
                  </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="profile-billing-address-line2">{t('register.address_line2_label', { defaultValue: '建物名・部屋番号（任意）' })}</Form.Label>
                        <Form.Control
                          id="profile-billing-address-line2"
                          type="text"
                          {...registerProfile('billingAddressLine2')}
                          autoComplete="address-line2"
                          placeholder={t('register.address_line2_placeholder', { defaultValue: '101号室' })}
                        />
                        <Form.Text className="text-muted">{t('register.address_line2_example', { defaultValue: '例: 101号室' })}</Form.Text>
                  </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="profile-billing-phone">{t('register.phone_label', { defaultValue: '電話番号' })}</Form.Label>
                    <Form.Control 
                      id="profile-billing-phone" 
                      type="tel" 
                      {...registerProfile('billingPhone')} 
                      autoComplete="tel"
                      pattern="[\d\+\-\(\)\s]+"
                          placeholder={t('register.phone_placeholder', { defaultValue: '03-1234-5678' })}
                    />
                        <Form.Text className="text-muted">{t('register.phone_example', { defaultValue: '例: 03-1234-5678' })}</Form.Text>
                  </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="profile-billing-invoice-reg-number">
                          {t('register.invoice_reg_number_label', { defaultValue: 'インボイス登録番号' })} ({t('common.optional', { defaultValue: '任意' })})
                        </Form.Label>
                        <Form.Control
                          id="profile-billing-invoice-reg-number"
                          type="text"
                          {...registerProfile('billingInvoiceRegNumber')}
                          placeholder={t('register.invoice_reg_number_placeholder', { defaultValue: 'T1234567890123, 123456789, GB123456789 など' })}
                          maxLength={50}
                        />
                        <Form.Text className="text-muted">
                          {t('register.invoice_reg_number_note', { defaultValue: '個人事業主または法人の場合のみ必要です。各国の形式に対応しています（例: 日本 T1234567890123、米国 123456789、英国 GB123456789 など）' })}
                        </Form.Text>
                  </Form.Group>
                    </Col>
                  </Row>
                </div>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={profileMutation.isPending}
                >
                  {profileMutation.isPending ? t('common.saving', { defaultValue: '保存中...' }) : t('profile.save')}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

          <Tab 
            eventKey="password" 
            title={t('profile.password_change')}
          >
          <Card className="mt-3">
            <Card.Header>
              <Card.Title className="mb-0">{t('profile.password_change')}</Card.Title>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmitPassword(onSubmitPassword)}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="profile-current-password">
                    {t('profile.current_password')} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    id="profile-current-password"
                    type="password"
                    {...registerPassword('currentPassword')}
                    isInvalid={!!passwordErrors.currentPassword}
                    autoComplete="current-password"
                  />
                  <Form.Control.Feedback type="invalid">
                    {passwordErrors.currentPassword?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor="profile-new-password">
                    {t('profile.new_password')} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    id="profile-new-password"
                    type="password"
                    {...registerPassword('newPassword')}
                    isInvalid={!!passwordErrors.newPassword}
                    autoComplete="new-password"
                  />
                  <Form.Control.Feedback type="invalid">
                    {passwordErrors.newPassword?.message}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">{t('common.password_min_length', { defaultValue: '8文字以上で入力してください' })}</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor="profile-confirm-password">
                    {t('profile.confirm_password')} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    id="profile-confirm-password"
                    type="password"
                    {...registerPassword('confirmPassword')}
                    isInvalid={!!passwordErrors.confirmPassword}
                    autoComplete="new-password"
                  />
                  <Form.Control.Feedback type="invalid">
                    {passwordErrors.confirmPassword?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={passwordMutation.isPending}
                >
                  {passwordMutation.isPending ? t('common.saving', { defaultValue: '保存中...' }) : t('profile.password_change')}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Tab>

          <Tab 
            eventKey="preferences" 
            title={t('profile.preferences')}
          >
          <Card className="mt-3">
            <Card.Header>
              <Card.Title className="mb-0">
                <i className="fas fa-cog me-2"></i>
                {t('profile.timezone')}・{t('profile.language')}設定
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmitPreferences(onSubmitPreferences)}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="profile-timezone">
                        {t('profile.timezone')} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        id="profile-timezone"
                        {...registerPreferences('timezone')}
                        isInvalid={!!preferencesErrors.timezone}
                      >
                        {Object.entries(groupedTimezones).map(([region, timezones]) => (
                          <optgroup key={region} label={region}>
                            {timezones.map((tz) => (
                              <option key={tz.value} value={tz.value}>
                                {tz.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {preferencesErrors.timezone?.message}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        {t('profile.timezone', { defaultValue: 'タイムゾーン' })}を設定します
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="profile-language">
                        {t('profile.language')} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        id="profile-language"
                        {...registerPreferences('language')}
                        isInvalid={!!preferencesErrors.language}
                      >
                        {getLanguages(t).map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.nativeName} ({lang.name})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {preferencesErrors.language?.message}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        {t('profile.language', { defaultValue: '言語' })}を設定します
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={preferencesMutation.isPending}
                >
                  {preferencesMutation.isPending ? t('common.saving', { defaultValue: '保存中...' }) : t('profile.preferences') + ' ' + t('profile.save')}
                </Button>
              </Form>
            </Card.Body>
          </Card>
          </Tab>

          <Tab
            eventKey="notifications"
            title={t('announcements.settings_tab', { defaultValue: 'お知らせメール' })}
          >
            <NotificationSettingsCard />
          </Tab>
        </Tabs>
      </div>
    </Container>
  );
}

